import React, { useState, useEffect } from 'react';
import { Question } from '../../types/Survey';
import './QuestionTypes.css';

interface RankingQuestionProps {
  question: Question;
  value?: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
}

const RankingQuestion: React.FC<RankingQuestionProps> = ({
  question,
  value = [],
  onChange,
  disabled = false
}) => {
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [rankedItems, setRankedItems] = useState<string[]>([]);
  const [unrankedItems, setUnrankedItems] = useState<string[]>([]);

  useEffect(() => {
    if (question.rankingItems) {
      const ranked = value.filter(item => question.rankingItems!.includes(item));
      const unranked = question.rankingItems.filter(item => !value.includes(item));
      setRankedItems(ranked);
      setUnrankedItems(unranked);
    }
  }, [question.rankingItems, value]);

  const handleDragStart = (e: React.DragEvent, item: string) => {
    if (disabled) return;
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropOnRanked = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    if (!draggedItem || disabled) return;

    const newRankedItems = [...rankedItems];
    const newUnrankedItems = [...unrankedItems];

    // Remove from current position
    const draggedFromRanked = rankedItems.includes(draggedItem);
    if (draggedFromRanked) {
      const currentIndex = newRankedItems.indexOf(draggedItem);
      newRankedItems.splice(currentIndex, 1);
    } else {
      const currentIndex = newUnrankedItems.indexOf(draggedItem);
      newUnrankedItems.splice(currentIndex, 1);
    }

    // Insert at new position
    newRankedItems.splice(targetIndex, 0, draggedItem);

    setRankedItems(newRankedItems);
    setUnrankedItems(newUnrankedItems);
    onChange(newRankedItems);
    setDraggedItem(null);
  };

  const handleDropOnUnranked = (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedItem || disabled) return;

    const newRankedItems = rankedItems.filter(item => item !== draggedItem);
    const newUnrankedItems = [...unrankedItems];

    if (!newUnrankedItems.includes(draggedItem)) {
      newUnrankedItems.push(draggedItem);
    }

    setRankedItems(newRankedItems);
    setUnrankedItems(newUnrankedItems);
    onChange(newRankedItems);
    setDraggedItem(null);
  };

  const moveItemUp = (index: number) => {
    if (index === 0 || disabled) return;
    const newRankedItems = [...rankedItems];
    [newRankedItems[index - 1], newRankedItems[index]] = [newRankedItems[index], newRankedItems[index - 1]];
    setRankedItems(newRankedItems);
    onChange(newRankedItems);
  };

  const moveItemDown = (index: number) => {
    if (index === rankedItems.length - 1 || disabled) return;
    const newRankedItems = [...rankedItems];
    [newRankedItems[index], newRankedItems[index + 1]] = [newRankedItems[index + 1], newRankedItems[index]];
    setRankedItems(newRankedItems);
    onChange(newRankedItems);
  };

  if (!question.rankingItems) {
    return <div className="error">순위 질문 구성이 올바르지 않습니다.</div>;
  }

  return (
    <div className="ranking-question">
      <div className="ranking-instructions">
        아래 항목들을 드래그하여 순위를 매겨주세요. (1순위가 가장 위에 위치)
      </div>
      
      <div className="ranking-container">
        <div className="ranked-items">
          <h4>순위</h4>
          <div 
            className="ranking-list"
            onDragOver={handleDragOver}
          >
            {rankedItems.length === 0 ? (
              <div 
                className="empty-ranking-area"
                onDrop={(e) => handleDropOnRanked(e, 0)}
              >
                여기로 항목을 드래그하세요
              </div>
            ) : (
              rankedItems.map((item, index) => (
                <div
                  key={item}
                  className={`ranking-item ranked ${draggedItem === item ? 'dragging' : ''}`}
                  draggable={!disabled}
                  onDragStart={(e) => handleDragStart(e, item)}
                  onDrop={(e) => handleDropOnRanked(e, index)}
                  onDragOver={handleDragOver}
                >
                  <span className="rank-number">{index + 1}</span>
                  <span className="item-text">{item}</span>
                  <div className="ranking-controls">
                    <button
                      type="button"
                      onClick={() => moveItemUp(index)}
                      disabled={index === 0 || disabled}
                      className="rank-button"
                      title="위로 이동"
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      onClick={() => moveItemDown(index)}
                      disabled={index === rankedItems.length - 1 || disabled}
                      className="rank-button"
                      title="아래로 이동"
                    >
                      ▼
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        
        <div className="unranked-items">
          <h4>항목</h4>
          <div 
            className="unranked-list"
            onDragOver={handleDragOver}
            onDrop={handleDropOnUnranked}
          >
            {unrankedItems.map((item) => (
              <div
                key={item}
                className={`ranking-item unranked ${draggedItem === item ? 'dragging' : ''}`}
                draggable={!disabled}
                onDragStart={(e) => handleDragStart(e, item)}
              >
                <span className="item-text">{item}</span>
                <span className="drag-handle">⋮⋮</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RankingQuestion;